import { useCallback, useEffect, useState } from "react";
import { CompareRajasthan } from "../../../models/rajasthan";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import {
  getGnaTgnaData,
  getRajasthanCollectiveData,
  getRajasthanDiscomData,
  getRajasthanGenerationData,
  getRajasthanIsgsData,
  getRajReData,
} from "../../../Rest_api/RestApi";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import {
  formatDateForAPI,
  getLast7DaysDateRange,
  R_CHART_TYPES,
} from "../const";
import FilterMenu from "../../../components/FilterMenu";
import { DynamicDatePicker } from "../../../components/DatePicker";
import Loading from "../../../components/Loading";
import { ChartType, LegendKey } from "../../../models/chart_model";
import { SearchBox } from "../../../components/Search";
import { renderHourTick } from "../../../components/recharts/components";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import SecondaryHeader from "../Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { getColor } from "../../../utility/Colors";

const colors = [
  "orange",
  "red",
  "blue",
  "Maroon",
  "brown",
  "green",
  "darkgreen",
  "purple",
  "black",
  "cyan",
  "magenta",
  "gold",
  "teal",
  "navy",
];

const SHORT_TERM_LEGENDS = [
  {
    key: "OA_GNA Buy",
    label: "OA GNA Buy",
    color: ExchangeColors[8],
    stackId: "generation",
  },
  {
    key: "OA_TGNA Buy",
    label: "OA TGNA Buy",
    color: ExchangeColors[7],
    stackId: "generation",
  },
  {
    key: "Collective Buy",
    label: "Collective Buy",
    color: ExchangeColors[4],
    stackId: "generation",
  },
  {
    key: "OA_GNA Sell",
    label: "OA GNA Sell",
    color: "black",
    stackId: "sell",
  },
  {
    key: "OA_TGNA Sell",
    label: "OA TGNA Sell",
    color: "purple",
    stackId: "sell",
  },
  {
    key: "Collective Sell",
    label: "Collective Sell",
    color: "magenta",
    stackId: "sell",
  },
];
const Y_AXIS = ["Price", "Short Term Procurement %"];

const LEGENDS = [
  {
    key: "ISGS",
    label: "Interstate Generation",
    color: ExchangeColors[3],
    stackId: "generation",
  },

  {
    key: "Intra Generation",
    label: "Intrastate Generation",
    color: ExchangeColors[0],
    stackId: "generation",
  },
  {
    key: "IntraState RE Generation",
    label: "IntraState RE Generation",
    color: "#00FF00",
    stackId: "generation",
  },
  {
    key: "InterState RE Generation",
    label: "OA RE Buy",
    color: "green",
    stackId: "generation",
  },
];

const DISCOM_LEGENDS = [
  // AVVNL Discom, JVVNL Discom, JDVVNL Discom
  {
    key: "AVVNL Discom",
    label: "AVVNL Discom",
    stackId: "discom",

    color: ExchangeColors[1],
  },
  {
    key: "JVVNL Discom",
    label: "JVVNL Discom",
    stackId: "discom",

    color: ExchangeColors[2],
  },
  {
    key: "JDVVNL Discom",
    label: "JDVVNL Discom",
    stackId: "discom",

    color: ExchangeColors[3],
  },
];

const TOTAL_DISCOM_LEGENDS = [
  {
    key: "Total Discom",
    label: "Discom Load",
    color: ExchangeColors[1],
    stackId: "discom",
  },
];

const KEYS = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "time_block",
    secondKey: "date",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];

const UNITS = ["MW", "MWh"];
const CONSUMPTION = [
  {
    label: "Total",
    value: TOTAL_DISCOM_LEGENDS,
  },
  {
    label: "By Discom",
    value: DISCOM_LEGENDS,
  },
];

export default function RajOverview() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [data, setData] = useState<CompareRajasthan[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [discomData, setDiscomData] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [uniqueEntities, setUniqueEntities] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);

  const [selectedConsumption, setSelectedConsumption] = useState(
    CONSUMPTION[0]
  );
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
  const [legends, setLegends] = useState<LegendKey[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[1]);

  const getActivity = useCallback(() => {
    return [
      { label: "Consumption", value: selectedConsumption.value },
      { label: "Generation", value: LEGENDS },
      {
        label: "Buy",
        value: SHORT_TERM_LEGENDS.filter((item) =>
          item.stackId.includes("generation")
        ),
      },
      {
        label: "Sell",
        value: SHORT_TERM_LEGENDS.filter((item) =>
          item.stackId.includes("sell")
        ),
      },
    ];
  }, [selectedConsumption.value]);
  const [selectedActivity, setSelectedActivity] = useState(getActivity());

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);
    // Initiate all API calls concurrently
    const [
      isgsData,
      collectiveData,
      gnaTgnaData,
      generationData,
      discomData,
      reGenData,
    ] = await Promise.all([
      getRajasthanIsgsData({ startDate: startDateStr, endDate: endDateStr }),
      getRajasthanCollectiveData({
        startDate: startDateStr,
        endDate: endDateStr,
      }),
      getGnaTgnaData({ startDate: startDateStr, endDate: endDateStr }),
      getRajasthanGenerationData(startDateStr, endDateStr),
      getRajasthanDiscomData(startDateStr, endDateStr),
      getRajReData({ startDate: startDateStr, endDate: endDateStr }),
    ]);
    // Process the data concurrently
    const processedIsgsData = isgsData.map((item) => ({
      ...item,
      value_mwh: item.value_mw / 4,
      entity: item.from_utility__name + " " + "ISGS Station",
      type: "ISGS",
      time_block: item.slot,
      month: getMonthFromDate(item.date),
    }));
    // .filter((item) => item.value_mw > 0);

    const processedCollectiveData = collectiveData.map((item) => {
      const isBuy = item.value_mw > 0;
      return {
        ...item,
        type: isBuy ? "Collective Buy" : "Collective Sell",
        time_block: item.time_slot,
        entity: item.exchange__name + " " + item.product__name,
        value_mwh: Math.abs(item.value_mw) / 4,
        value_mw: Math.abs(item.value_mw),
        month: getMonthFromDate(item.date),
      };
    });
    const processedGnaTgnaData = gnaTgnaData
      .map((item) => {
        const isBuy = item.value_mw > 0;
        return {
          ...item,
          type: `${item.category__category} ${isBuy ? "Buy" : "Sell"}`,
          entity: item.from_utility__name,
          toEntity: item.to_utility__name,
          value_mwh: Math.abs(item.value_mw) / 4,
          value_mw: Math.abs(item.value_mw),
          month: getMonthFromDate(item.date),
        };
      })
      // .filter((item) => item.value_mw > 0);
    const processedGenerationData = generationData.map((item) => ({
      ...item,
      type: "Intra Generation",
      value_mwh: item.onBarDS_mw! / 4,
      entity: item.station + " " + "Intra Generator",
      value_mw: item.onBarDS_mw!,
      time_block: item.slot,
      month: getMonthFromDate(item.date),
    }));

    const processedReData = reGenData.map((item) => ({
      ...item,
      type: item.area + " RE Generation",
      value_mwh: item.value_mw / 4,
      entity: item.energy_type + " RE",
    }));

    // Combine all processed data
    const newData: CompareRajasthan[] = [
      ...processedIsgsData,
      ...processedCollectiveData,
      ...processedGnaTgnaData,
      ...processedGenerationData,
      ...processedReData,
    ];
    // unique entities are unique entity and toEntity
    const uniqueEntities = Array.from(
      new Set(newData.map((item) => [item.entity, item.toEntity]).flat())
    ).filter((item) => item !== undefined) as string[];
    
    // console.log("uniqueEntities",uniqueEntities)
    setUniqueEntities(uniqueEntities);
    setDiscomData(discomData);
    setLoading(false);
    setData(newData);
  }, [activeTab, DefaultDates]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    setSelectedActivity(getActivity());
  }, [getActivity, selectedConsumption]);

  useEffect(() => {
    const processedDiscomData = discomData.map((item) => ({
      ...item,
      type:
        (selectedConsumption.label === "Total" ? "Total" : item.discom) +
        " Discom",
      value_mwh: (item.drawal_mw + item.load_shedding_mw) / 4,
      value_mw: item.drawal_mw + item.load_shedding_mw,
      time_block: item.block,
      month: getMonthFromDate(item.date),
    }));

    let filteredData = data.concat(processedDiscomData);
    if (selectedEntity.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          selectedEntity.includes(item.entity!) ||
          selectedEntity.includes(item.toEntity!)
      );
    }

    const params: ChartDataParams = {
      keys: selectedKey.secondKey
        ? [selectedKey.firstKey, selectedKey.secondKey]
        : [selectedKey.firstKey],
      tableKeys: ["date", "time_block"],
      groupBy: ["type"],
      volumeKey: "value_mwh",
      aggregationKeys: ["value_mw"],
      priceKey: "",
      averageDuplicationKeys: ["time_block", "date"],
      averageKeys: ["value_mw"],
      reverseSort: ["date", "time_block"],
      data: filteredData,
    };

    const chartData = FormatChartData(params);

    let finalData = chartData.data.map((item) => {
      // Check for valid data structure first
      if (!item) return { shortTerm: 0 };

      // Calculate total with safety checks
      const total = selectedConsumption.value.reduce((acc, column) => {
        const value = item[column.key + "_value_" + selectedUnit.toLowerCase()];
        return acc + (isNaN(value) ? 0 : Number(value) || 0);
      }, 0);

      // Calculate short term with safety checks
      const shortTerm = SHORT_TERM_LEGENDS.filter(
        (e) => e.key === "Collective Buy" || e.key === "OA_TGNA Buy"
      )
        .map((legend) => legend.key + "_value_" + selectedUnit.toLowerCase())
        .reduce((acc, column) => {
          const value = item[column];
          return acc + (isNaN(value) ? 0 : Number(value) || 0);
        }, 0);

      // Calculate percentage with extra safety
      let percent = 0;
      if (total > 0 && !isNaN(shortTerm) && !isNaN(total)) {
        percent = (shortTerm / total) * 100;
      }

      // Debug logging to identify problematic data points
      if (isNaN(percent)) {
        console.log("NaN detected:", {
          date: item.date,
          timeBlock: item.time_block,
          shortTerm,
          total,
        });
        percent = 0; // Fallback to 0 if still NaN
      }

      const dataItems = data.filter((dataItem) => {
        return (
          item.time_slot === dataItem.time_block &&
          item.date === dataItem.date &&
          (dataItem.type == "Collective Buy" ||
            dataItem.type == "Collective Sell")
        );
      });

      const iex_dam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "DAM"
      ) as any;
      const iex_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "RTM"
      ) as any;
      const iex_gdam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "GDAM"
      ) as any;
      const pxil_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "PXIL" && dataItem.product__name === "RTM"
      ) as any;

      return {
        ...item,
        shortTerm: Number(percent.toFixed(2)) > 0 ? percent.toFixed(2) : 0,
        IEX_DAM_value_mw: iex_dam ? iex_dam.wt_mcp_rs_kwh : 0,
        IEX_RTM_value_mw: iex_rtm ? iex_rtm.wt_mcp_rs_kwh : 0,
        IEX_GDAM_value_mw: iex_gdam ? iex_gdam.wt_mcp_rs_kwh : 0,
        PXIL_RTM_value_mw: pxil_rtm ? pxil_rtm.wt_mcp_rs_kwh : 0,
      };
    });

    let finalTableData = chartData.tableData?.map((item) => {
      // Check for valid data structure first
      if (!item) return { shortTerm: 0 };

      // Calculate total with safety checks
      const total = selectedConsumption.value.reduce((acc, column) => {
        const value = item[column.key + "_value_" + selectedUnit.toLowerCase()];
        return acc + (isNaN(value) ? 0 : Number(value) || 0);
      }, 0);

      // Calculate short term with safety checks
      const shortTerm = SHORT_TERM_LEGENDS.filter(
        (e) => e.key === "Collective Buy" || e.key === "OA_TGNA Buy"
      )
        .map((legend) => legend.key + "_value_" + selectedUnit.toLowerCase())
        .reduce((acc, column) => {
          const value = item[column];
          return acc + (isNaN(value) ? 0 : Number(value) || 0);
        }, 0);

      // Calculate percentage with extra safety
      let percent = 0;
      if (total > 0 && !isNaN(shortTerm) && !isNaN(total)) {
        percent = (shortTerm / total) * 100;
      }

      // Debug logging to identify problematic data points
      if (isNaN(percent)) {
        console.log("NaN detected:", {
          date: item.date,
          timeBlock: item.time_block,
          shortTerm,
          total,
        });
        percent = 0; // Fallback to 0 if still NaN
      }

      const dataItems = data.filter((dataItem) => {
        return dataItem;
      });

      const iex_dam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "DAM"
      ) as any;
      const iex_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "RTM"
      ) as any;
      const iex_gdam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "GDAM"
      ) as any;
      const pxil_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "PXIL" && dataItem.product__name === "RTM"
      ) as any;

      return {
        ...item,
        shortTerm: Number(percent.toFixed(2)) > 0 ? percent.toFixed(2) : 0,
        IEX_DAM_value_mw: iex_dam ? iex_dam.wt_mcp_rs_kwh : 0,
        IEX_RTM_value_mw: iex_rtm ? iex_rtm.wt_mcp_rs_kwh : 0,
        IEX_GDAM_value_mw: iex_gdam ? iex_gdam.wt_mcp_rs_kwh : 0,
        PXIL_RTM_value_mw: pxil_rtm ? pxil_rtm.wt_mcp_rs_kwh : 0,
      };
    });

    setChartData(finalData);
    setChartKeys(chartData.keys);
    setTableData(finalTableData || []);
  }, [
    data,
    selectedKey,
    selectedConsumption,
    selectedEntity,
    discomData,
    selectedUnit,
  ]);

  const handleUnitChange = (index: number) => {
    setSelectedUnit(UNITS[index]);
  };
  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  const summaryExtraKeys = selectedKey.isTime
    ? [
        "time_block", // Changed dateIndex to date
        selectedKey.values[1],
      ]
    : [selectedKey.values[0]];

  const summaryExtraLabels = selectedKey.values.map(
    (value) => (value === "date" ? "Date" : value) // Simplified label mapping
  );

  const tablePar = {
    legends: getActiveLegends(),
    data: chartData,
    tableData: tableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Overview By Summary",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };
  return (
    <div className="page-container">
      {loading && <Loading />}
      <div className="gas-page-container">
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Interval"
              items={KEYS.map((item) => item.name)}
              selectedItems={[selectedKey.name]}
              onSelect={(item, index) => {
                handleKeyChange(index);
              }}
            />
            <FilterMenu
              title="Unit"
              items={UNITS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                handleUnitChange(index);
              }}
            />
            <FilterMenu
              title="Load"
              items={CONSUMPTION.map((item) => item.label)}
              selectedItems={[selectedConsumption.label]}
              onSelect={(item, index) => {
                setSelectedConsumption(CONSUMPTION[index]);
              }}
            />
            <FilterMenu
              title="Activity"
              items={getActivity().map((item) => item.label)}
              selectedItems={selectedActivity.map((item) => item.label)}
              setSelectedItems={(v: string[]) => {
                setSelectedActivity(
                  getActivity().filter((item) => v.includes(item.label)) as any
                );
              }}
            />
            <FilterMenu
              title="Chart Type"
              items={R_CHART_TYPES.map((item) => item.label)}
              selectedItems={[selectedChartType.label]}
              onSelect={(item, index) => {
                setSelectedChartType(R_CHART_TYPES[index]);
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader />
          <div className="gas-chart-container">
            <div className="gas-heading">
              <h2 className="chartHeading w-60">By Summary</h2>
              <div className="flex gap-x-6  justify-end">
                <SearchBox
                  options={uniqueEntities.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  isMany={true}
                  placeholder="Search Entities"
                  selectedOptions={selectedEntity.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  onChange={(item: any) => {
                    setSelectedEntity(item.map((item: any) => item.value));
                  }}
                />

                <button
                  onClick={() => {
                    dataTab.label === "Chart" &&
                      downloadChartTableCSV(tablePar);
                    dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                    dataTab.label === "Table" &&
                      downloadChartTableCSV(tablePar);
                  }}
                  className="downloadButton"
                >
                  Download Data
                </button>
              </div>
            </div>

            {dataTab.label === "Chart" && (
              <ReMixChart
                data={chartData}
                xDataKey={selectedKey.firstKey}
                xTick={
                  selectedKey.secondKey !== undefined
                    ? (value: any, index: any) => {
                        return renderHourTick(value, 10);
                      }
                    : undefined
                }
                secondXDataKey={selectedKey.secondKey}
                unit={selectedUnit}
                isTimeSlot={selectedKey.firstKey === "time_block"}
                onlyTitle={true}
                secondYAxisLabel="Short Term Procurement %"
                barCategoryGap={12}
                yAxisLabel={selectedUnit}
                showBrush={getShowBrush(chartData)}
                brushIndex={getBrushIndex()}
                legends={getActiveLegends()}
              />
            )}
            {dataTab.label === "Summary" && (
              <LegendSummaryTable {...tablePar} />
            )}
            {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={<div>RUVITL, Eltrix, WBES</div>}
                text="1. Short Term Procurement % = (Collective + TGNA)/Total Discom Load 
                2. Intrastate Generation = Eltrix schedule
                3. Intrastate RE Generation =  NREB Grid Curves

4. Interstate Generation = ISGS WBES
5. MW = Represents the average (MW) when the selected time interval is Date or Month.
"
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </div>
  );

  function getActiveLegends() {
    const activityLegends =
      selectedActivity.length === 0
        ? selectedConsumption.value.concat(LEGENDS).concat(SHORT_TERM_LEGENDS)
        : selectedActivity.map((item) => item.value).flat();

    return activityLegends
      .map((legend, index) => {
        return {
          dataKey: legend.key + "_value_" + selectedUnit.toLowerCase(),
          name: legend.label,
          legendColor: undefined,
          stroke: getColor(index, legend.label),
          stackId: selectedChartType.stackId ? legend.stackId : undefined,
          type: selectedChartType.value,
        };
      })
      .concat([
        {
          dataKey: "shortTerm",
          name: "Short Term Procurement %",
          yAxisId: "right",
          stroke: getColor(34, "Short Term Procurement %"),
          stackId: "generation",
          type: ChartType.Line,
        } as any,
      ]) as LegendKey[];
  }

  function getShowBrush(chartData: any[]): boolean {
    if (selectedKey.firstKey === "time_block") {
      return chartData.length > 96 * 1;
    } else {
      return chartData.length > 7;
    }
  }

  function getBrushIndex(): { startIndex: number; endIndex: number } {
    if (selectedKey.firstKey === "time_block") {
      return { startIndex: 0, endIndex: 96 * 1 };
    } else {
      return { startIndex: 0, endIndex: 7 };
    }
  }
}
